import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { selectLoading, addAlert } from "src/modules/notification/redux";
import {
  putWhiteLabel,
  getCompanies,
  setCompanyStatus,
  removeCompanyWhiteLabel,
  clearCompanies,
  clearCompanyUsers,
  getCompanyUsers,
} from "../../redux";
import CompanyDetailsDialog from "../../components/dialogs/company-details-dialog";
import CompanySwapPlanDialog from "../../components/dialogs/company-plan-swap-dialog";
import CompanySerialNumberAssignDialog from "../../components/dialogs/company-assign-sn-dialog";
import CompanyChangeStatusDialog from "../../components/dialogs/company-change-status-dialog/companyChangeStatusDialog.container";
import CompaniesPage from "./companies.page";
import AccessRights from "../../../../configs/constants/accessRights";
import CompanyExpiredDateDialog from "../../components/dialogs/company-expire-date-dialog";
import CompanySalesEmailDialog from "../../components/dialogs/company-sales-email-dialog";

// The search bar stores option LABELS but the API wants their values. Written out longhand
// because this repo's eslint parser cannot parse `?.` — returns undefined for no match, the
// same as the `find(...)?.value` this replaces.
const labelledValue = (options, label) => {
  const match = (options || []).find((item) => item.label === label);
  return match ? match.value : undefined;
};

export default function CompaniesContainer() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const industries = useSelector((state) => state.constants.industries);
  const isLoading = useSelector((state) =>
    selectLoading(state, getCompanies.typePrefix)
  );
  const companies = useSelector((state) => state.companies.companies);
  const companiesPage = useSelector((state) => state.companies.companiesPage);
  const totalCompanies = useSelector((state) => state.companies.totalCompanies);
  const allBranchWhiteLabelCount = useSelector(
    (state) => state.companies.allBranchWhiteLabelCount
  );
  const [selectedSwapPlanCompany, setSelectedSwapPlanCompany] = useState(null);
  const [selectedViewCompany, setSelectedViewCompany] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [query, setQuery] = useState("");

  const [changeStatusDialog, setChangeStatusDialog] = useState("hidden");
  const [selectedChangeStatusCompany, setSelectedChangeStatusCompany] =
    useState(null);

  const [
    selectedSerialNumberAssignCompany,
    setSelectedSerialNumberAssignCompany,
  ] = useState(null);
  const [itemPerPage, setItemPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(0);
  // The last submitted search (companyName/regNo, plan, status) and the Expired Date sort
  // (null = the server's default order, else "asc" | "desc"). Every list load re-sends both,
  // so paging or a refresh never silently drops them.
  const [appliedFilter, setAppliedFilter] = useState({});
  const [expiredSort, setExpiredSort] = useState(null);

  const canView = useSelector((state) =>
    state.session.accessRights.includes(AccessRights.COMPANY_VIEW)
  );
  const canEdit = useSelector((state) =>
    state.session.accessRights.includes(AccessRights.COMPANY_EDIT)
  );
  const canViewWhiteLabel = useSelector((state) =>
    state.session.accessRights.includes(AccessRights.COMPANY_WHITE_LABEL_VIEW)
  );
  const canViewBranch = useSelector((state) =>
    state.session.accessRights.includes(AccessRights.COMPANY_BRANCH_VIEW)
  );
  const canViewUser = useSelector((state) =>
    state.session.accessRights.includes(AccessRights.COMPANY_USER_VIEW)
  );
  const canAssignSn = useSelector((state) =>
    state.session.accessRights.includes(AccessRights.COMPANY_ASSIGN)
  );

  const [selectedCompanyForExpiredDate, setSelectedCompanyForExpiredDate] =
    useState(null);
  const [shouldRefresh, setShouldRefresh] = useState(false);

  const handleEditDateClick = () => {
    setShouldRefresh(true);
  };

  const initialFilter = {
    type: "Company Name",
    query: "",
    plan: "",
    status: "",
  };
  const typeList = [
    { label: "Company Name", value: "company_name" },
    { label: "Registration Number", value: "reg_no" },
  ];
  const statusList = [
    { label: "Approved", value: "approved" },
    { label: "Deactivated", value: "deactivated" },
  ];
  const planList = [
    { label: "Pro", value: "pro" },
    { label: "Lite", value: "lite" },
  ];

  // `suppressErrorAlert` is for refreshes the admin did not ask for and whose failure says
  // nothing about what they did ask for — see onCompanyFeatureDialogSubmit. Every other
  // caller leaves it off and a failed load is alerted as usual.
  const fetchData = (options) => {
    dispatch(
      getCompanies({
        page: currentPage,
        length: itemPerPage,
        searchQuery: query,
        ...appliedFilter,
        expiredSort,
        suppressErrorAlert: Boolean(options && options.suppressErrorAlert),
      })
    );
  };

  useEffect(() => {
    fetchData();

    return () => {
      dispatch(clearCompanies());
    };
  }, [dispatch, itemPerPage]);

  useEffect(() => {
    if (!canView) {
      navigate("/admin");
    }
  }, [canView]);

  const onSearchSubmit = (values) => {
    const selectedType = labelledValue(typeList, values.type);
    const selectedPlan = labelledValue(planList, values.plan);
    const selectedStatus = labelledValue(statusList, values.status);
    const filter = {
      plan: selectedPlan,
      status: selectedStatus,
    };
    if (selectedType === "company_name") filter.companyName = values.query;
    if (selectedType === "reg_no") filter.regNo = values.query;
    setAppliedFilter(filter);
    setCurrentPage(0);
    dispatch(
      getCompanies({ page: 0, length: itemPerPage, ...filter, expiredSort })
    );
  };

  // First click puts the soonest expiry first; each click after that flips the direction.
  const onExpiredSortClick = () => {
    const next = expiredSort === "asc" ? "desc" : "asc";
    setExpiredSort(next);
    setCurrentPage(0);
    dispatch(
      getCompanies({
        page: 0,
        length: itemPerPage,
        ...appliedFilter,
        expiredSort: next,
      })
    );
  };

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Called by the Company Features tab of the details dialog once it has saved and said so
  // (the dialog stays open). This row refresh is incidental to the save that already
  // succeeded, and the snackbar is a QUEUE, so a red toast from it would arrive seconds after
  // the green one and read as the save having failed. It did not. A rejection leaves the list
  // one save out of date, which the next load or search corrects.
  const onCompanyFeatureDialogSubmit = () => {
    fetchData({ suppressErrorAlert: true });
  };

  const onCompanyDashboardViewClick = (company) => {
    navigate(`/admin/companies/${company.company.id}`);
  };

  const onCompanyViewClick = (company) => {
    setSelectedViewCompany(company);
  };

  const onCompanyDialogCloseClick = () => {
    setSelectedViewCompany(null);
  };

  const onCompanyWhiteLabelDialogSubmitClick = (values, { setSubmitting }) => {
    dispatch(
      putWhiteLabel({
        id: selectedViewCompany.company.id,
        whiteLabel: values.whiteLabel,
      })
    ).then(() => {
      dispatch(
        addAlert({
          severity: "success",
          message: "Company's white label updated successfully",
        })
      );
      setCurrentPage(0);
      dispatch(
        getCompanies({
          page: 0,
          length: itemPerPage,
          ...appliedFilter,
          expiredSort,
        })
      );
      setSubmitting(false);
    });
  };

  const onRemoveCompanyWhiteLabelClick = () => {
    dispatch(
      removeCompanyWhiteLabel({
        id: selectedViewCompany.company.id,
      })
    ).then(() => {
      dispatch(
        addAlert({
          severity: "success",
          message: "Company's white label remove successfully",
        })
      );
      dispatch(
        getCompanies({
          page: currentPage,
          length: itemPerPage,
          ...appliedFilter,
          expiredSort,
        })
      );
    });
  };

  const onCompaniesPageClick = (page) => {
    if (companiesPage !== page) {
      setCurrentPage(page);
      dispatch(
        getCompanies({
          page,
          length: itemPerPage,
          searchQuery: query,
          ...appliedFilter,
          expiredSort,
        })
      );
    }
  };

  const onCompanyUsersViewClick = async ({
    companyId,
    companyPlan,
    page = 0,
    length = 10,
  }) => {
    await dispatch(
      getCompanyUsers({
        companyId,
        companyPlan,
        page,
        length,
      })
    );
  };

  const handleItemPerPageChange = (value) => {
    setItemPerPage(value);
  };

  const onSerialNumberAssignClick = async ({ company }) => {
    setSelectedSerialNumberAssignCompany(company);
  };

  const onSerialNumberAssignDialogCloseClick = () => {
    setSelectedSerialNumberAssignCompany(null);
  };

  const onEnableClick = (company) => {
    setChangeStatusDialog("enable");
    setSelectedChangeStatusCompany(company);
  };

  const onDisableClick = (company) => {
    setChangeStatusDialog("disable");
    setSelectedChangeStatusCompany(company);
  };

  const onChangeStatus = async (action, remark) => {
    const actionToStatus = {
      enable: "approved",
      disable: "deactivated",
    };

    await dispatch(
      setCompanyStatus({
        id: selectedChangeStatusCompany,
        status: actionToStatus[action],
        remarks: remark,
      })
    );

    await fetchData();
  };

  const clearSearch = () => {
    setAppliedFilter({});
    setExpiredSort(null);
    setCurrentPage(0);
    const data = {
      page: 0,
      length: itemPerPage,
      plan: "",
      status: "",
    };
    dispatch(getCompanies(data));
  };

  // Sales Email Dialog State
  const [selectedCompanyForSalesEmail, setSelectedCompanyForSalesEmail] =
    useState(null);

  // Sales Email Dialog Handlers
  const onCompanySalesEmailDialogCloseClick = () => {
    setSelectedCompanyForSalesEmail(null);
  };

  const onCompanySalesEmailDialogSubmitClick = (values) => {
    // TODO: Dispatch your update action here, e.g.:
    // dispatch(updateCompanySalesEmail({ id: selectedCompanyForSalesEmail.company.id, salesEmail: values.salesEmail }))
    //   .then(() => {
    //     dispatch(addAlert({ severity: "success", message: "Sales email updated successfully" }));
    //     setSelectedCompanyForSalesEmail(null);
    //     fetchData();
    //   });
  };

  useEffect(() => {
    if (shouldRefresh) {
      fetchData();
      setShouldRefresh(false);
    }
  }, [shouldRefresh]);

  return (
    <>
      <CompaniesPage
        industries={industries}
        isCompaniesLoading={isLoading}
        companies={companies}
        totalCompanies={totalCompanies}
        companiesPage={companiesPage}
        onCompanyViewClick={onCompanyViewClick}
        onCompanyDashboardViewClick={onCompanyDashboardViewClick}
        onCompaniesPageClick={onCompaniesPageClick}
        onCompanyUsersViewClick={onCompanyUsersViewClick}
        onCompanySwapPlanClick={(row) => setSelectedSwapPlanCompany(row)}
        onSerialNumberAssignClick={onSerialNumberAssignClick}
        initialFilter={initialFilter}
        typeList={typeList}
        statusList={statusList}
        planList={planList}
        onSearchSubmit={onSearchSubmit}
        expiredSort={expiredSort}
        onExpiredSortClick={onExpiredSortClick}
        handleItemPerPageChange={handleItemPerPageChange}
        itemPerPage={itemPerPage}
        allBranchWhiteLabelCount={allBranchWhiteLabelCount}
        open={open}
        anchorEl={anchorEl}
        handleOpen={handleOpen}
        handleClose={handleClose}
        selectedRow={selectedRow}
        setSelectedRow={setSelectedRow}
        onEnableClick={onEnableClick}
        onDisableClick={onDisableClick}
        clearSearch={clearSearch}
        canEdit={canEdit}
        canViewWhiteLabel={canViewWhiteLabel}
        canViewBranch={canViewBranch}
        canViewUser={canViewUser}
        canAssignSn={canAssignSn}
        setSelectedCompanyForExpiredDate={setSelectedCompanyForExpiredDate}
        setSelectedCompanyForSalesEmail={setSelectedCompanyForSalesEmail}
      />
      <CompanyDetailsDialog
        industries={industries}
        selectedCompany={selectedViewCompany}
        onCompanyDetailsDialogCloseClick={onCompanyDialogCloseClick}
        onCompanyWhiteLabelDialogSubmitClick={
          onCompanyWhiteLabelDialogSubmitClick
        }
        onRemoveCompanyWhiteLabelClick={onRemoveCompanyWhiteLabelClick}
        onCompanyUsersViewClick={onCompanyUsersViewClick}
        onCompanyFeatureSaved={onCompanyFeatureDialogSubmit}
      />

      <CompanySwapPlanDialog
        selectedCompany={
          selectedSwapPlanCompany ? selectedSwapPlanCompany.company : null
        }
        onCompanySwapPlanDialogCloseClick={(needUpdate) => {
          setSelectedSwapPlanCompany(null);

          if (needUpdate) {
            fetchData();
          }
        }}
      />
      <CompanySerialNumberAssignDialog
        selectedCompany={selectedSerialNumberAssignCompany}
        onSerialNumberAssignDialogCloseClick={
          onSerialNumberAssignDialogCloseClick
        }
      />
      <CompanyChangeStatusDialog
        isOpen={changeStatusDialog !== "hidden"}
        action={changeStatusDialog}
        handleConfirm={async (action, remark) => {
          await onChangeStatus(action, remark);

          setSelectedChangeStatusCompany(null);
          setChangeStatusDialog("hidden");
        }}
        handleClose={() => {
          setSelectedChangeStatusCompany(null);
          setChangeStatusDialog("hidden");
        }}
      />
      {selectedCompanyForExpiredDate && (
        <CompanyExpiredDateDialog
          selectedCompany={selectedCompanyForExpiredDate}
          onCompanyDialogCloseClick={() =>
            setSelectedCompanyForExpiredDate(null)
          }
          onEditDateClick={handleEditDateClick}
        />
      )}

      {selectedCompanyForSalesEmail && (
        <CompanySalesEmailDialog
          selectedCompany={selectedCompanyForSalesEmail}
          onCompanySalesEmailDialogCloseClick={
            onCompanySalesEmailDialogCloseClick
          }
          onCompanySalesEmailDialogSubmitClick={
            onCompanySalesEmailDialogSubmitClick
          }
        />
      )}
    </>
  );
}
